export interface RangeLimits {
  min?: number;
  max?: number;
}

export enum TooltipSeverity {
  Error = 'error',
  Warning = 'warning',
}

const SEVERITY_VERB: Record<TooltipSeverity, string> = {
  [TooltipSeverity.Error]: 'must',
  [TooltipSeverity.Warning]: 'should',
};

export function getRangeTooltip(
  subject: string,
  limits: RangeLimits,
  unit: string,
  severity: TooltipSeverity = TooltipSeverity.Error
): string {
  const unitSuffix = !unit ? '' : unit.startsWith('°') || unit === '%' ? unit : ` ${unit}`;
  const verb = SEVERITY_VERB[severity];

  if (limits.min != null && limits.max != null) {
    return `${subject} ${verb} be between ${limits.min} and ${limits.max}${unitSuffix}`;
  }
  if (limits.min != null) {
    return `${subject} ${verb} be at least ${limits.min}${unitSuffix}`;
  }
  if (limits.max != null) {
    return `${subject} ${verb} be at most ${limits.max}${unitSuffix}`;
  }
  return `${subject} ${verb} be provided`;
}

export function isOutOfRange(value: number | undefined, limits: RangeLimits): boolean {
  if (value == null) return false;
  return (limits.min != null && value < limits.min) || (limits.max != null && value > limits.max);
}

export interface HardValidationResult {
  invalid: boolean;
  tooltip: string;
}

/**
 * Combines a required-field check with a hard min/max range check, for fields whose
 * legacy V1 validation blocks report submission (native HTML5 min/max/required rolled
 * into `reportForm.$invalid`) rather than merely showing an advisory warning.
 */
export function getHardValidation(
  value: number | undefined,
  limits: RangeLimits,
  subject: string,
  unit: string,
  required = false
): HardValidationResult {
  const isMissing = required && value == null;
  const invalid = isMissing || isOutOfRange(value, limits);
  const tooltip = isMissing ? `${subject} is required` : getRangeTooltip(subject, limits, unit, TooltipSeverity.Error);

  return { invalid, tooltip };
}
